<template>
  <v-card flat>
    <v-card-text>
      <!-- ================================================================= -->
      <!-- HEADER & TABLE                                                  -->
      <!-- ================================================================= -->
      <div class="d-flex justify-space-between align-center mb-4">
        <h3 class="headline">Item-Bibliothek verwalten</h3>
        <v-btn color="default" dark @click="openItemEditor(null)">
          <v-icon left>mdi-plus</v-icon>
          Neues Item erstellen
        </v-btn>
      </div>
      <p>Diese Items können in mehreren Checklisten wiederverwendet werden. Änderungen an einem Item hier wirken sich auf alle Checklisten aus, die es verwenden.</p>

      <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :search="search"
          class="elevation-1"
      >
        <template v-slot:top>
          <v-text-field v-model="search" label="Suchen..." class="mx-4"></v-text-field>
        </template>
        <template v-slot:item.video_path="{ item }">
          <v-icon v-if="item.video_path" color="info" @click.stop="playVideo(item.video_path)">mdi-play-circle</v-icon>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" title="Item bearbeiten" @click="openItemEditor(item)">mdi-pencil</v-icon>
          <v-icon small color="red" title="Item löschen" @click="confirmDeleteItem(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- ================================================================= -->
    <!-- DIALOGS                                                         -->
    <!-- ================================================================= -->
    <!-- Dialog 1: The Main Item Editor -->
    <v-dialog v-model="dialog.edit" persistent max-width="900px">
      <v-card>
        <v-card-title><span class="headline">{{ isEditing ? 'Item bearbeiten' : 'Neues Item erstellen' }}</span></v-card-title>
        <v-card-text>
          <v-alert v-if="isEditing && itemUsageList.length > 0" type="warning" dense border="left" class="mb-4">
            Dieses Item wird in <strong>{{ itemUsageList.length }}</strong> Checkliste(n) verwendet. Änderungen hier werden überall übernommen.
            <ul v-if="itemUsageList.length < 5"><li v-for="usage in itemUsageList" :key="usage._id">{{ usage.identifier }}</li></ul>
          </v-alert>
          <v-text-field v-model="editableItem.title" label="Titel" required outlined class="mb-4"></v-text-field>
          <vue-editor v-if="dialog.edit" v-model="editableItem.body" :editorOptions="editorSettings" useCustomImageHandler @image-added="handleImageAdded"></vue-editor>

          <v-divider class="my-4"></v-divider>
          <div class="subtitle-1 font-weight-bold">Video</div>
          <div v-if="isEditing && editableItem.video_path && !wantsToRemoveVideo" class="d-flex align-center pa-2 grey lighten-4 rounded">
            <v-icon class="mr-2">mdi-video</v-icon>
            <span class="text-truncate">{{ editableItem.video_path }}</span>
            <v-spacer></v-spacer>
            <v-btn text small color="red" @click="wantsToRemoveVideo = true">Video entfernen</v-btn>
          </div>
          <v-file-input v-else v-model="videoFileToUpload" label="Video hochladen/ersetzen" accept="video/*" outlined dense prepend-icon="mdi-video-plus"></v-file-input>
          <v-checkbox v-if="wantsToRemoveVideo" v-model="wantsToRemoveVideo" label="Video permanent entfernen" readonly></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeItemEditor">Abbrechen</v-btn>
          <v-btn color="primary" text :loading="isSavingItem" @click="saveItem">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog 2: Delete Confirmation -->
    <v-dialog v-model="dialog.delete" max-width="500px">
      <v-card v-if="itemToDelete">
        <v-card-title class="headline">Item "{{ itemToDelete.title }}" löschen?</v-card-title>
        <!-- Case 1: Item is in use -->
        <div v-if="itemUsageList.length > 0">
          <v-card-text>
            <v-alert type="error" prominent border="left">
              Dieses Item kann nicht gelöscht werden, da es aktuell in <strong>{{ itemUsageList.length }} Checkliste(n)</strong> verwendet wird. Sie müssen es zuerst aus allen Checklisten entfernen.
            </v-alert>
            <ul><li v-for="usage in itemUsageList" :key="usage._id">{{ usage.identifier }}</li></ul>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialog.delete = false">Verstanden</v-btn>
          </v-card-actions>
        </div>
        <!-- Case 2: Item is not in use -->
        <div v-else>
          <v-card-text>
            Sind Sie sicher, dass Sie dieses Item permanent löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialog.delete = false">Abbrechen</v-btn>
            <v-btn color="red darken-1" text :loading="isDeleting" @click="deleteItem">Löschen</v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog 3: Video Player -->
    <v-dialog v-model="videoPlayerDialog" max-width="800px">
      <v-card>
        <v-card-text class="pa-4">
          <video v-if="videoPlayerDialog" :src="videoSource" controls style="width: 100%;"></video>
        </v-card-text>
        <v-card-actions><v-spacer></v-spacer><v-btn text @click="videoPlayerDialog = false">Schliessen</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.state" :timeout="snack.timeout" :color="snack.color" top right>{{ snack.message }}</v-snackbar>
  </v-card>
</template>

<script>
import axios from 'axios';
import { VueEditor, Quill } from "vue2-editor";
import ImageResize from 'quill-image-resize-vue';

Quill.register("modules/imageResize", ImageResize);
const defaultItem = { title: '', body: '', video_path: null };

export default {
  name: "ItemLibrary",
  components: { VueEditor },
  data() {
    return {
      loading: false,
      isSavingItem: false,
      isDeleting: false,
      search: '',
      items: [],
      dialog: { edit: false, delete: false },
      isEditing: false,
      editableItem: { ...defaultItem },
      itemToDelete: null,
      itemUsageList: [],

      // Video State
      videoFileToUpload: null,
      wantsToRemoveVideo: false,
      videoPlayerDialog: false,
      videoSource: '',

      editorSettings: { modules: { imageResize: {} } },
      headers: [
        { text: 'Titel', align: 'start', value: 'title' },
        { text: 'Video', value: 'video_path', sortable: false, align: 'center'},
        { text: 'Aktionen', value: 'actions', sortable: false, align: 'end' },
      ],
      snack: { state: false, color: 'success', timeout: 3000, message: '' },
    };
  },
  mounted() {
    this.fetchItems();
  },
  methods: {
    fetchItems() {
      this.loading = true;
      axios.get('/items')
          .then(response => { this.items = response.data; })
          .catch(() => this.showSnackbar("Bibliothek konnte nicht geladen werden.", "error"))
          .finally(() => this.loading = false);
    },

    // --- Create / Edit Logic ---
    async openItemEditor(item) {
      this.itemUsageList = [];
      this.closeItemEditor(); // Reset state

      if (item) { // Editing
        this.isEditing = true;
        this.editableItem = { ...item };
        try {
          const response = await axios.get(`/items/${item._id}/usage`);
          this.itemUsageList = response.data;
        } catch (e) { this.showSnackbar("Item-Verwendungsdaten konnten nicht geladen werden.", "error"); }
      } else { // Creating
        this.isEditing = false;
        this.editableItem = { ...defaultItem };
      }
      this.dialog.edit = true;
    },
    closeItemEditor() {
      this.dialog.edit = false;
      this.videoFileToUpload = null;
      this.wantsToRemoveVideo = false;
    },
    saveItem() {
      this.isSavingItem = true;
      const formData = new FormData();
      formData.append('title', this.editableItem.title);
      formData.append('body', this.editableItem.body);
      if (this.videoFileToUpload) formData.append('video', this.videoFileToUpload);
      if (this.wantsToRemoveVideo) formData.append('remove_video', 'true');

      const promise = this.isEditing
          ? axios.patch(`/items/${this.editableItem._id}`, formData)
          : axios.post('/items', formData);

      promise.then(() => {
        this.showSnackbar(`Item "${this.editableItem.title}" gespeichert.`);
        this.fetchItems();
        this.closeItemEditor();
      })
          .catch(() => this.showSnackbar("Item konnte nicht gespeichert werden.", "error"))
          .finally(() => this.isSavingItem = false);
    },

    // --- Delete Logic ---
    async confirmDeleteItem(item) {
      this.itemToDelete = item;
      this.itemUsageList = [];
      this.isDeleting = true; // Use this to show a loading state if needed
      this.dialog.delete = true;
      try {
        const response = await axios.get(`/items/${item._id}/usage`);
        this.itemUsageList = response.data;
      } catch (e) {
        this.showSnackbar("Item-Verwendungsdaten konnten nicht geladen werden.", "error");
        this.dialog.delete = false; // Close dialog on error
      } finally {
        this.isDeleting = false;
      }
    },
    deleteItem() {
      this.isDeleting = true;
      axios.delete(`/items/${this.itemToDelete._id}`)
          .then(() => {
            this.showSnackbar(`Item "${this.itemToDelete.title}" gelöscht.`);
            this.fetchItems();
            this.dialog.delete = false;
          })
          .catch(() => this.showSnackbar("Item konnte nicht gelöscht werden.", "error"))
          .finally(() => this.isDeleting = false);
    },

    // --- Utility Methods ---
    playVideo(videoPath) {
      this.videoSource = process.env.VUE_APP_STATIC_URL + '/uploads/' + videoPath;
      this.videoPlayerDialog = true;
    },
    handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      let formData = new FormData();
      formData.append("file", file);
      axios.post('/uploads/image', formData)
          .then(result => {
            const url = process.env.VUE_APP_STATIC_URL + '/uploads/' + result.data.url;
            Editor.insertEmbed(cursorLocation, "image", url);
            resetUploader();
          })
          .catch(() => {
            this.showSnackbar("Bild konnte nicht hochgeladen werden.", "error");
            resetUploader();
          });
    },
    showSnackbar(message, color = 'success') {
      this.snack.message = message;
      this.snack.color = color;
      this.snack.state = true;
    },
  },
};
</script>